import { MultiAutoCompleteItem } from "@/components/MultiAutoComplete/MultiAutoComplete";
import { SENIORITY_OPTIONS } from "@/pages/Private/pages/CompanyEnrich/constants/seniority.constants";

/**
 * The seniority a job title denotes, tried in ladder order (highest first), so
 * a title naming two levels resolves to the higher one ("Senior Director" is a
 * director, not a senior).
 *
 * Every pattern tolerates a plural, because the personas are the user's own
 * words ("CEOs", "VPs"), and German spellings, because the ICP chat is answered
 * in the user's own language. The German ones drop the leading word boundary —
 * those titles compound ("Vertriebsleiterin").
 */
const SENIORITY_PATTERNS: { id: string; pattern: RegExp }[] = [
	{ id: "owner", pattern: /\b(owners?|proprietors?)\b|inhaber(in)?(nen)?\b/ },
	{ id: "founder", pattern: /\b(co[-\s]?)?founders?\b|gr(ü|ue)nder(in)?(nen)?\b/ },
	{
		id: "c-suite",
		pattern:
			/\b(chiefs?|c[etfomripdlcsx]os?|cisos?|chros?|managing directors?|presidents?|board members?)\b|gesch(ä|ae)ftsf(ü|ue)hrer(in)?(nen)?\b|vorstand(s|smitglied)?\b/,
	},
	{ id: "partner", pattern: /\bpartners?\b|partnerin(nen)?\b/ },
	{ id: "vp", pattern: /\b([sea]?vps?|vice presidents?)\b|vizepr(ä|ae)sident(in)?(en|nen)?\b/ },
	{ id: "head", pattern: /\bheads?\b|leiter(in)?(nen)?\b|leitung\b/ },
	{ id: "director", pattern: /\bdirectors?\b|direktor(in)?(en|nen)?\b/ },
	{ id: "manager", pattern: /\bteam ?leads?\b|\blead of\b|manager(in)?(nen)?\b/ },
	{ id: "senior", pattern: /\b(seniors?|sr\.?)\b/ },
	{ id: "intern", pattern: /\b(interns?|internships?)\b|praktikant(in)?(en|nen)?\b/ },
	{
		id: "entry",
		pattern: /\b(juniors?|jr\.?|entry|associates?|trainees?)\b|werkstudent(in)?(en|nen)?\b/,
	},
];

const seniorityOf = (title: string): string | null =>
	SENIORITY_PATTERNS.find(({ pattern }) => pattern.test(title.toLowerCase()))?.id ?? null;

/**
 * The seniority filter that stands in for a set of job titles — the widening
 * the Track step falls back to when the literal titles match nobody (a resolved
 * title like "Chief Revenue Officer (CRO)" exists in the index but may have no
 * holder inside the user's target companies, while "c-suite" does).
 *
 * Titles that denote no level at all are simply skipped; if none of them map,
 * the caller gets an empty list and searches without a role filter.
 */
export const senioritiesFromTitles = (titles: string[]): MultiAutoCompleteItem[] => {
	const found = new Set(titles.map(seniorityOf).filter((id): id is string => id !== null));

	// Ordered by the picker's own ladder so the pane shows them the way it lists them.
	return SENIORITY_OPTIONS.filter((option) => found.has(option.id));
};
